import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

const QUERY_PARAM = 'customerView';

/**
 * "View as customer" mode: hides purchase prices and the app menu so the screen can be shown to a customer.
 * The state is mirrored in the `?customerView=1` query param so it survives a reload.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerViewService {
  #router = inject(Router);

  #url = toSignal(
    this.#router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.#router.url },
  );

  /** The toggle is only offered on the articles overview and details pages. */
  readonly isAvailable = computed(() => /^\/products\/articles(\/|\?|$)/.test(this.#url()));

  readonly #toggled = signal(false);

  /** Only active on article pages, so leaving them never leaves the menu hidden without a way back. */
  readonly isActive = computed(() => this.isAvailable() && this.#toggled());

  constructor() {
    this.#router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.#syncWithUrl());
  }

  set(value: boolean) {
    this.#toggled.set(value);
    this.#writeParam(value);
  }

  /** Restores the mode from the URL on load, and re-adds the param when a link inside the articles dropped it. */
  #syncWithUrl() {
    if (!this.isAvailable()) {
      return;
    }

    const hasParam = this.#router.parseUrl(this.#router.url).queryParamMap.has(QUERY_PARAM);

    if (hasParam) {
      this.#toggled.set(true);
    } else if (this.#toggled()) {
      this.#writeParam(true);
    }
  }

  #writeParam(value: boolean) {
    this.#router.navigate([], {
      queryParams: { [QUERY_PARAM]: value ? 1 : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
